import React, { useMemo, useRef, useState } from "react"
import { VirtuosoHandle } from "react-virtuoso"

import { SECTION_CLASSES } from "../../../shared-components/section-v2/section-classes"
import { classNames } from "../../../utils/class-names"
import CatalogFilters from "./catalog-filters"
import CatalogTable from "./catalog-table"
import useCatalogData from "./catalog-table/use-catalog-data"
import DownloadCatalogButton from "./catalog-tabs/download-catalog-button"
import * as styles from "./index.module.scss"
import {
  ALL_CATEGORIES,
  CatalogFilterValue,
  CATEGORY_CARD_TYPE_MAP,
  CategoryProps,
} from "./types"
import {
  getUniqueOptions,
  itemNameMatchesSearch,
  sortByCountriesCount,
} from "./utils"

function CatalogPanel({
  downloadable,
  category: categoryParam,
}: CategoryProps) {
  const [filterValues, setFilters] = useState<CatalogFilterValue>({
    category: categoryParam ?? ALL_CATEGORIES,
  })

  const virtuoso = useRef<VirtuosoHandle>(null)

  const handleSetFilters = (filters: CatalogFilterValue) => {
    setFilters(filters)
    virtuoso.current?.scrollToIndex({
      index: 0,
    })
  }

  const { catalogItems } = useCatalogData()

  const filteredCatalogItems = useMemo(() => {
    const items = catalogItems ?? []
    return items.filter(catalogItem => {
      const { country, currencyCode, name, category } = filterValues
      const countryMatches =
        !country || catalogItem.countryCodes.includes(country)
      const currencyMatches =
        !currencyCode || catalogItem.currencyCodes.includes(currencyCode)
      const nameMatches = !name || itemNameMatchesSearch(catalogItem, name)
      const categoryCardTypes = CATEGORY_CARD_TYPE_MAP[category]
      const categoryMatches =
        !categoryCardTypes || categoryCardTypes.includes(catalogItem.category)
      return countryMatches && currencyMatches && nameMatches && categoryMatches
    })
  }, [catalogItems, filterValues])
  const { category } = filterValues

  const { countryOptions, currencyOptions } = useMemo(() => {
    return getUniqueOptions(catalogItems ?? [])
  }, [catalogItems])

  return (
    <div className={classNames(styles.section, SECTION_CLASSES.light)}>
      <div className={styles.container}>
        <CatalogFilters
          filterValues={filterValues}
          setFilters={handleSetFilters}
          countryOptions={countryOptions}
          currencyOptions={currencyOptions}
          numberOfResults={filteredCatalogItems.length}
          disabled={!catalogItems}
        />
        {downloadable && (
          <div className={styles.downloadButtonContainer}>
            <DownloadCatalogButton disabled={!catalogItems} />
          </div>
        )}
        <div className={styles.container}>
          <CatalogTable
            ref={virtuoso}
            catalogItems={
              category === "Charities"
                ? filteredCatalogItems.sort(sortByCountriesCount)
                : filteredCatalogItems
            }
          />
        </div>
      </div>
    </div>
  )
}

export default CatalogPanel
